import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { BhcHeader } from '@/components/health-check/BhcHeader';
import { BhcFooter } from '@/components/health-check/BhcFooter';
import '@/styles/business-health-check.css';
import { useJsonLd } from '@/hooks/useJsonLd';

const QUALIFY = '/business-health-check/qualifier';
const TRUST = '/business-health-check/trust-estate';

/* Funnel imagery — fitting, license-safe stock served same-origin (swap for KGOB brand photos anytime). */
const IMG_HERO = '/business-health-check/hero-advisor.jpg';   // advisor & client reviewing together
const IMG_REPORT = '/business-health-check/findings-report.jpg'; // findings / financial dashboard

/** Image slot — shows a real photo when `src` is given, else the styled placeholder. */
const Ph = ({ tag, ratio = '4/5', style, src, alt }) => (
  <div className="ph" style={{ aspectRatio: ratio, ...style }}>
    {src
      ? <img src={src} alt={alt || tag} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      : <span className="ph__tag">{tag}</span>}
  </div>
);

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Do I really pay before I speak to someone?', acceptedAnswer: { '@type': 'Answer', text: "Yes — and here's why that's a good thing. The qualifier confirms you're a fit before you pay, so there are no surprises. The moment your payment clears, you're a client: a member of our team is assigned to your engagement and reaches out directly with next steps. You're not buying a conversation — you're starting the actual work." } },
    { '@type': 'Question', name: 'Is the fee refundable?', acceptedAnswer: { '@type': 'Answer', text: 'No. This is a fixed-fee engagement and work begins promptly after you sign the engagement terms and submit your records. Because we begin work right away, the fee is non-refundable once work commences — which is exactly what you agree to before paying. We qualify your fit beforehand specifically so you can pay with confidence.' } },
    { '@type': 'Question', name: 'How long does it take?', acceptedAnswer: { '@type': 'Answer', text: 'We deliver your findings report and schedule your strategy meeting within 15 business days of receiving your complete records — not from your payment date. The clock starts when we have everything we need.' } },
    { '@type': 'Question', name: 'What if my business is bigger than the included scope?', acceptedAnswer: { '@type': 'Answer', text: "If your situation exceeds the included transaction volume, entity count, or states, we'll pause and send you a separate fixed-price quote before doing any extra work. You're always in control." } },
    { '@type': 'Question', name: 'Do you need access to my IRS account?', acceptedAnswer: { '@type': 'Answer', text: "To review your IRS and state accounts, we'll have you sign a Form 2848 (Power of Attorney), which authorizes us to access your IRS and state records and notices and act on your behalf for the items we review. Any tax-resolution or representation work beyond this diagnostic is quoted separately." } },
    { '@type': 'Question', name: 'Is my financial information secure?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. You will upload your documents through our secure client portal, never by email. All transfers are encrypted in transit and at rest.' } },
    { '@type': 'Question', name: 'What happens after the strategy meeting?', acceptedAnswer: { '@type': 'Answer', text: "If the review surfaces work worth doing — catching up prior years, fixing a payroll or sales-tax issue, ongoing bookkeeping — we'll give you a clear, separate, fixed-price proposal. There's no obligation; the Health Check stands on its own." } },
    { '@type': 'Question', name: 'Do you offer this for trusts and estates?', acceptedAnswer: { '@type': 'Answer', text: 'Yes — we offer a version built specifically for trustees and executors. See the Trust & Estate Health Check.' } },
  ],
};

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org', '@type': 'AccountingService',
  name: 'Kohari Gonzalez Oneyear & Brown PLLC', alternateName: 'KGOB', url: 'https://kgob.com/',
  telephone: '+1-704-599-3355', priceRange: '$$',
  address: { '@type': 'PostalAddress', streetAddress: '2740 East WT Harris Blvd, Suite 200', addressLocality: 'Charlotte', addressRegion: 'NC', postalCode: '28213', addressCountry: 'US' },
  areaServed: 'US',
  makesOffer: { '@type': 'Offer', price: '4995.00', priceCurrency: 'USD', itemOffered: { '@type': 'Service', name: 'Mid-Year Business Health Check', serviceType: 'Fixed-fee business financial and tax diagnostic', description: 'A fixed-fee CPA diagnostic: bookkeeping catch-up (Jan-Jun), financial statement preparation, tax projection, and payroll/sales-tax/registration compliance review, with a written findings report and a 60-minute strategy meeting.', provider: { '@type': 'AccountingService', name: 'Kohari Gonzalez Oneyear & Brown PLLC' } } },
};

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://kgob.com/' },
    { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://kgob.com/services' },
    { '@type': 'ListItem', position: 3, name: 'Mid-Year Business Health Check', item: 'https://kgob.com/business-health-check' },
  ],
};

const INCLUDED = [
  ['01', 'Bookkeeping catch-up (Jan 1 – Jun 30)', 'Up to 2 bank accounts and 2 credit cards, organized and categorized so the numbers actually mean something.'],
  ['02', 'Financial statement preparation', 'A clear picture of profit, cash, and where the money is going.'],
  ['03', 'Tax projection & estimated liability', "An estimate of what you're likely to owe, so December holds no surprises."],
  ['04', 'Tax savings opportunity review', 'A look at deductions, elections, and timing that may reduce your exposure.'],
  ['05', 'Payroll compliance review', 'A check for gaps that commonly trigger penalties.'],
  ['06', 'Sales tax compliance review', 'A check of your sales-tax position in your primary state.'],
  ['07', 'Registration & annual report review', "Confirmation you're current on your business filings."],
  ['08', 'IRS & state account review', 'A review of your tax accounts and notices, with your authorization.'],
  ['09', 'Written findings report', 'Everything we found, in plain English, with a prioritized action plan.'],
  ['10', '60-minute strategy meeting', 'A working session with our team to walk through findings and next steps.'],
];

const FAQS = [
  ['Do I really pay before I speak to someone?', "Yes — and here's why that's a good thing. The qualifier confirms you're a fit before you pay, so there are no surprises. The moment your payment clears, you're a client: a member of our team is assigned to your engagement and reaches out directly with next steps. You're not buying a conversation — you're starting the actual work.", true],
  ['Is the fee refundable?', 'No. This is a fixed-fee engagement and work begins promptly after you sign the engagement terms and submit your records. Because we begin work right away, the fee is non-refundable once work commences — which is exactly what you agree to before paying. We qualify your fit beforehand specifically so you can pay with confidence.', false],
  ['How long does it take?', 'We deliver your findings report and schedule your strategy meeting within 15 business days of receiving your complete records — not from your payment date. The clock starts when we have everything we need.', false],
  ['What if my business is bigger than the included scope?', "If your situation exceeds the included transaction volume, entity count, or states, we'll pause and send you a separate fixed-price quote before doing any extra work. You're always in control.", false],
  ['Do you need access to my IRS account?', "To review your IRS and state accounts, we'll have you sign a Form 2848 (Power of Attorney), which authorizes us to access your IRS and state records and notices and act on your behalf for the items we review. Any tax-resolution or representation work beyond this diagnostic is quoted separately.", false],
  ['Is my financial information secure?', 'Yes. You will upload your documents through our secure client portal, never by email. All transfers are encrypted in transit and at rest.', false],
  ['What happens after the strategy meeting?', "If the review surfaces work worth doing — catching up prior years, fixing a payroll or sales-tax issue, ongoing bookkeeping — we'll give you a clear, separate, fixed-price proposal. There's no obligation; the Health Check stands on its own.", false],
];

export default function BusinessHealthCheckLanding() {
    useJsonLd('bhc-landing-0', SERVICE_SCHEMA);
  useJsonLd('bhc-landing-1', FAQ_SCHEMA);
  useJsonLd('bhc-landing-2', BREADCRUMB_SCHEMA);

  return (
    <div className="bhc-root bhc-landing">
      <Helmet>
        <title>Mid-Year Business Health Check | Fixed-Fee CPA Review | KGOB</title>
        <meta name="description" content="Know exactly where your business stands before year-end. A fixed-fee CPA diagnostic: books, tax projection & compliance review. Limited engagements." />
        <link rel="canonical" href="https://kgob.com/business-health-check" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,300;6..72,400;6..72,500&family=Newsreader:ital,opsz,wght@1,6..72,400&family=Figtree:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Mid-Year Business Health Check | Fixed-Fee CPA Review | KGOB" />
        <meta property="og:description" content="Organize your books, estimate your tax exposure, and get a clear action plan before year-end. One fixed fee. Limited engagements." />
        <meta property="og:url" content="https://kgob.com/business-health-check" />
      </Helmet>

      <BhcHeader
        nav={[
          { label: "What's included", href: '#what' },
          { label: 'How it works', href: '#how' },
          { label: 'Pricing', href: '#pricing' },
          { label: 'FAQ', href: '#faq' },
          { label: 'Trust & Estate', to: TRUST },
        ]}
        cta={{ label: 'See If You Qualify', to: QUALIFY }}
      />

      <main>
        {/* HERO */}
        <section className="heroA wrap" data-testid="hero">
          <div className="heroA__grid">
            <div className="heroA__copy">
              <span className="eyebrow">Mid-Year Business Health Check · Fixed Fee</span>
              <h1 className="display-1" style={{ marginTop: '22px' }}>Know exactly where your business stands — <span className="em-italic">before year-end surprises.</span></h1>
              <p className="heroA__sub lede">You're halfway through the year. Do you actually know if you're making money, whether you're behind on taxes, or if there's a problem you can't see yet? We'll find out — and hand you a clear plan.</p>
              <ul className="fearstrip" aria-label="Common concerns">
                <li>"Am I actually making money?"</li>
                <li>"Am I behind on my taxes?"</li>
                <li>"Is something wrong I can't see?"</li>
              </ul>
              <div className="offerline">
                <span className="offerline__price">$4,995</span>
                <span className="offerline__label">One fixed fee. One clear answer.<br />No hourly surprises.</span>
              </div>
              <div className="heroA__cta">
                <Link className="btn btn--primary btn--lg" to={QUALIFY} data-testid="hero-cta">See If You Qualify <span className="arrow">→</span></Link>
                <p className="heroA__scarcity">Limited to <b>25 engagements</b> this quarter ·<br />Book before <b>September 30</b>.</p>
              </div>
              <div className="trust-strip">
                <span>Serving business owners since 1988</span><span className="dot" />
                <span>Licensed NC CPAs</span><span className="dot" />
                <span>Charlotte · Mooresville · Winston-Salem</span>
              </div>
            </div>
            <div className="heroA__media">
              <Ph tag="Advisor / client photo" ratio="4/5" src={IMG_HERO} alt="A KGOB advisor reviewing a business owner's mid-year numbers" style={{ boxShadow: 'var(--shadow-lg)' }} />
              <div className="heroA__badge">
                <b>10</b>
                <span>deliverables in one fixed-fee engagement</span>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM */}
        <section className="problem section" data-testid="problem">
          <div className="wrap problem__grid">
            <div>
              <span className="eyebrow eyebrow--on-dark">Sound familiar?</span>
              <h2 className="display-2" style={{ marginTop: '20px' }}>Most owners hit mid-year flying blind.</h2>
              <p className="lede" style={{ color: 'rgba(255,255,255,.72)', marginTop: '22px', maxWidth: '42ch' }}>The books are behind, the tax picture is fuzzy, and there's a quiet worry that something's off — you just don't know what.</p>
              <p className="problem__close" style={{ marginTop: '32px' }}>The problem isn't that you're bad at running your business. It's that no one has stopped to give you a straight, current read on where you actually stand.</p>
            </div>
            <div>
              <p style={{ fontSize: '15px', color: 'var(--gold-300)', marginBottom: '6px' }}>Left alone until filing season, uncertainty turns into real cost:</p>
              <ul className="costlist">
                <li><span className="n">01</span><span>A tax bill that's bigger than you planned for.</span></li>
                <li><span className="n">02</span><span>Underpayment penalties and interest that were avoidable.</span></li>
                <li><span className="n">03</span><span>A compliance gap — payroll, sales tax, a missed registration — quietly growing.</span></li>
                <li><span className="n">04</span><span>Months of catch-up work crammed into your most stressful season.</span></li>
              </ul>
            </div>
          </div>
        </section>

        {/* SOLUTION */}
        <section className="section" id="what" data-testid="solution">
          <div className="wrap solution__grid">
            <div className="solution__media">
              <Ph tag="Findings-report image" ratio="5/4" src={IMG_REPORT} alt="A clear written findings report with your mid-year financial picture" style={{ boxShadow: 'var(--shadow-md)' }} />
            </div>
            <div>
              <span className="eyebrow">The Solution</span>
              <h2 className="display-2" style={{ marginTop: '20px' }}>A fixed-fee diagnostic for your business's financial health.</h2>
              <p className="lede" style={{ marginTop: '22px' }}>In one engagement, our CPAs organize your books through June 30, estimate your tax exposure for the year, review your key compliance areas, and give you a <strong>written findings report</strong> plus a <strong>60-minute strategy session</strong> to walk through it.</p>
              <div className="physical-note">Think of it as a physical for your business: we run the diagnostics, tell you what's healthy, flag what needs attention, and give you a plan — before December turns problems into emergencies.</div>
            </div>
          </div>
        </section>

        {/* INCLUDED */}
        <section className="section--tight section" style={{ background: 'var(--card)', borderBlock: '1px solid var(--line)' }} data-testid="included">
          <div className="wrap">
            <div className="included__head">
              <div>
                <span className="eyebrow">What's Included</span>
                <h2 className="display-2" style={{ marginTop: '18px', maxWidth: '18ch' }}>Everything below is part of the single fixed fee.</h2>
              </div>
              <p className="muted measure" style={{ maxWidth: '34ch' }}>Ten deliverables, one price. No à la carte add-ons hiding in an invoice later.</p>
            </div>
            <div className="stack-grid">
              {INCLUDED.map(([no, h, p]) => (
                <div className="stack-item" key={no}><span className="stack-item__no">{no}</span><h3>{h}</h3><p>{p}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* RECEIVE */}
        <section className="receive section" data-testid="receive">
          <div className="wrap">
            <div className="center">
              <span className="eyebrow eyebrow--center">What You Receive</span>
              <h2 className="display-2" style={{ marginTop: '16px' }}>Two things you can act on immediately.</h2>
            </div>
            <div className="receive__grid">
              <div className="deliver">
                <div className="deliver__icon" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M7 3h7l5 5v13a0 0 0 0 1 0 0H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>
                </div>
                <h3>A written Findings Report</h3>
                <p>Your numbers organized, your estimated tax exposure, the compliance items we checked, and a clear, prioritized list of what to do next.</p>
              </div>
              <div className="deliver">
                <div className="deliver__icon" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                </div>
                <h3>A 60-minute strategy session</h3>
                <p>Not a sales call — a working meeting with our CPAs to make sure you understand exactly where you stand and what matters most before year-end.</p>
              </div>
            </div>
          </div>
        </section>

        {/* WHO */}
        <section className="section" data-testid="who">
          <div className="wrap">
            <div className="center" style={{ marginBottom: '44px' }}>
              <span className="eyebrow eyebrow--center">Who It's For</span>
              <h2 className="display-2" style={{ marginTop: '16px' }}>Honest about fit — on purpose.</h2>
            </div>
            <div className="who__grid">
              <div className="who-card who-card--yes">
                <h3>A strong fit if you…</h3>
                <ul>
                  <li><span className="mk">✓</span>Run a single business entity operating primarily in one state.</li>
                  <li><span className="mk">✓</span>Are a few months behind on bookkeeping and want to get current.</li>
                  <li><span className="mk">✓</span>Want a real tax estimate before year-end, not a filing-season surprise.</li>
                  <li><span className="mk">✓</span>Want one fixed price, not an open-ended cleanup bill.</li>
                </ul>
              </div>
              <div className="who-card who-card--no">
                <h3>Not the right starting point if you…</h3>
                <ul>
                  <li><span className="mk">—</span>Need multiple years of cleanup or have multiple entities.</li>
                  <li><span className="mk">—</span>Are already in an active dispute with the IRS or a state agency.</li>
                  <li><span className="mk">—</span>Need amended returns, prior-year filings, or formal representation today.</li>
                </ul>
              </div>
            </div>
            <p className="who__foot">If that's you, this isn't a dead end — <Link data-testid="landing-link-3" to="/business-health-check/custom-quote">start with a conversation</Link> and we'll build a <strong>custom proposal around your requirements.</strong></p>
          </div>
        </section>

        {/* HOW */}
        <section className="how section" id="how" data-testid="how">
          <div className="wrap">
            <div className="center">
              <span className="eyebrow eyebrow--center eyebrow--on-dark">How It Works</span>
              <h2 className="display-2" style={{ marginTop: '16px' }}>Five steps from question to clarity.</h2>
            </div>
            <div className="how__steps">
              <div className="step"><div className="step__no">1</div><h3>See if you qualify</h3><p>Answer a few quick questions so we can confirm your situation fits this fixed-fee engagement.</p></div>
              <div className="step"><div className="step__no">2</div><h3>Review terms &amp; pay</h3><p>Review exactly what's included, agree to the engagement terms, and pay your fixed fee securely.</p></div>
              <div className="step"><div className="step__no">3</div><h3>Send us your records</h3><p>We give you a simple, secure checklist of exactly what we need.</p></div>
              <div className="step"><div className="step__no">4</div><h3>We do the work</h3><p>Our CPAs organize your books, build your projection, and run the compliance reviews.</p></div>
              <div className="step"><div className="step__no">5</div><h3>Report &amp; strategy</h3><p>We deliver your written findings and sit down with you for the 60-minute meeting.</p></div>
            </div>
            <div className="how__reassure">
              <span className="ic" aria-hidden="true"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg></span>
              <p>"The moment your payment is confirmed, you're a KGOB client — a real member of our team is assigned to your engagement and reaches out directly. Real people, not a chatbot."</p>
            </div>
          </div>
        </section>

        {/* NOT INCLUDED */}
        <section className="section" data-testid="not-included">
          <div className="wrap notincl__grid">
            <div>
              <span className="eyebrow">What's Not Included</span>
              <h2 className="display-2" style={{ marginTop: '18px' }}>A diagnostic — not a catch-all.</h2>
              <p className="lede" style={{ marginTop: '20px' }}>To keep this a true fixed fee, the Health Check identifies issues; it doesn't fix everything inside the fee. Anything below is <strong>quoted separately, in writing, with a fixed price</strong> — so you're always in control of what happens next.</p>
            </div>
            <div>
              <ul className="tags">
                <li>Amended returns</li>
                <li>Tax resolution / back taxes</li>
                <li>IRS or state representation</li>
                <li>Prior-year &amp; multi-year cleanup</li>
                <li>Payroll corrections</li>
                <li>Sales-tax corrections / filings</li>
                <li>Entity restructuring</li>
              </ul>
              <p className="notincl__note">Any corrective work we identify during the review will be quoted separately — never quietly added to your bill.</p>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="pricing section" id="pricing" data-testid="pricing">
          <div className="wrap">
            <div className="pricecard">
              <div>
                <span className="eyebrow eyebrow--on-dark">Pricing</span>
                <h2 className="display-3" style={{ marginTop: '16px' }}>Mid-Year Business Health Check</h2>
                <div className="pricecard__price" style={{ marginTop: '8px' }}><sup>$</sup>4,995</div>
                <p style={{ color: 'var(--gold-300)', fontSize: '15px' }}>One fixed fee. All ten deliverables.</p>
                <p className="price-worth">A single missed quarterly estimate, an avoidable underpayment penalty, or a compliance gap discovered too late can cost more than this engagement — and cause a lot more stress. The Health Check surfaces those issues while you still have time to act.</p>
                <Link className="btn btn--primary btn--lg" to={QUALIFY} style={{ marginTop: '26px' }} data-testid="pricing-cta">See If You Qualify <span className="arrow">→</span></Link>
              </div>
              <div className="pricecard__sep" />
              <div>
                <p style={{ fontSize: '13px', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--gold-300)', marginBottom: '16px' }}>Your fixed fee covers</p>
                <ul className="pricecard__list">
                  <li><span className="tick">✓</span>Bookkeeping catch-up, Jan–Jun</li>
                  <li><span className="tick">✓</span>Financial statements</li>
                  <li><span className="tick">✓</span>Tax projection &amp; savings review</li>
                  <li><span className="tick">✓</span>Payroll &amp; sales-tax compliance</li>
                  <li><span className="tick">✓</span>Registration &amp; IRS account review</li>
                  <li><span className="tick">✓</span>Written findings report</li>
                  <li><span className="tick">✓</span>60-minute strategy meeting</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAIRNESS */}
        <section className="section" data-testid="fairness">
          <div className="wrap fair__grid">
            <div>
              <span className="eyebrow">The Fairness Note</span>
              <h2 className="display-2" style={{ marginTop: '18px' }}>Exactly how this fixed fee works.</h2>
              <p className="muted" style={{ marginTop: '18px', maxWidth: '32ch' }}>Upfront, in plain English — so there are no surprises on either side.</p>
            </div>
            <ul className="fair__list">
              <li><span className="ic" aria-hidden="true">◆</span><span>It covers <strong>one business entity</strong> operating in <strong>one primary state</strong>.</span></li>
              <li><span className="ic" aria-hidden="true">◆</span><span>It includes bookkeeping across up to <strong>2 bank accounts and 2 credit cards</strong> from January through June.</span></li>
              <li><span className="ic" aria-hidden="true">◆</span><span>We work from the <strong>records you provide</strong> — we organize and review them; we don't audit or independently verify them.</span></li>
              <li><span className="ic" aria-hidden="true">◆</span><span>The tax numbers are a <strong>projection / estimate</strong> based on the information you give us — not a guarantee of any final result.</span></li>
              <li><span className="ic" aria-hidden="true">◆</span><span>If your situation is larger than this, we'll <strong>pause and give you a separate quote</strong> before doing extra work — never a quiet bill.</span></li>
            </ul>
          </div>
        </section>

        {/* WHY KGOB */}
        <section className="why section" data-testid="why-kgob">
          <div className="wrap">
            <span className="eyebrow eyebrow--on-dark">Why KGOB</span>
            <h2 className="display-2" style={{ marginTop: '18px', maxWidth: '20ch' }}>Financial guidance is only as good as who's behind it.</h2>
            <div className="why__stats">
              <div className="stat"><b>1988</b><span>Serving business owners across the Carolinas</span></div>
              <div className="stat"><b>4</b><span>NC offices across Charlotte, Mooresville &amp; Winston-Salem</span></div>
              <div className="stat"><b>50</b><span>States served from our North Carolina practice</span></div>
            </div>
            <p style={{ color: 'rgba(255,255,255,.7)', maxWidth: '60ch', marginBottom: '38px' }}>Every Health Check is performed by our licensed team at <strong style={{ color: '#fff' }}>Kohari Gonzalez Oneyear &amp; Brown PLLC</strong> — and after you pay, you work with them directly.</p>
            <div><a data-testid="landing-a-1" className="btn btn--ghost-light btn--lg" href="https://kgob.com/our-story" target="_blank" rel="noopener noreferrer">Read the KGOB story <span className="arrow">→</span></a></div>
          </div>
        </section>

        {/* CREDENTIALS */}
        <section className="section" data-testid="trust-credentials">
          <div className="wrap">
            <div className="center">
              <span className="eyebrow eyebrow--center">Why owners trust KGOB</span>
              <h2 className="display-2" style={{ marginTop: '16px' }}>Built on credentials, not claims.</h2>
            </div>
            <ul className="credlist">
              <li><span className="tick">✓</span><span>Licensed North Carolina CPA firm — registration on file</span></li>
              <li><span className="tick">✓</span><span>Serving business owners since 1988</span></li>
              <li><span className="tick">✓</span><span>Four offices across Charlotte, Mooresville, and Winston-Salem</span></li>
              <li><span className="tick">✓</span><span>Every engagement performed and reviewed by licensed CPAs</span></li>
              <li><span className="tick">✓</span><span>Member, AICPA and NC Association of CPAs</span></li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" id="faq" style={{ background: 'var(--paper-2)' }} data-testid="faq">
          <div className="wrap faq__grid">
            <div>
              <span className="eyebrow">FAQ</span>
              <h2 className="display-2" style={{ marginTop: '18px' }}>Questions, answered straight.</h2>
              <p className="muted" style={{ marginTop: '18px', maxWidth: '30ch' }}>Still unsure? <Link data-testid="landing-link-2" to={QUALIFY} style={{ color: 'var(--navy-800)', borderBottom: '1px solid var(--gold-500)' }}>The qualifier</Link> confirms your fit before you pay anything.</p>
            </div>
            <div>
              {FAQS.map(([q, a, open]) => (
                <details data-testid="landing-details-2" className="faq-item" key={q} open={open}>
                  <summary data-testid="landing-summary-2">{q}<span className="pm">+</span></summary>
                  <div className="faq-item__body">{a}</div>
                </details>
              ))}
              <details data-testid="landing-details-1" className="faq-item">
                <summary data-testid="landing-summary-1">Do you offer this for trusts and estates?<span className="pm">+</span></summary>
                <div className="faq-item__body">Yes — we offer a version built specifically for trustees and executors. <Link data-testid="landing-link-1" to={TRUST} style={{ color: 'var(--navy-800)', borderBottom: '1px solid var(--gold-500)' }}>See the Trust &amp; Estate Health Check →</Link></div>
              </details>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="finalcta section" data-testid="final-cta">
          <div className="wrap finalcta__inner">
            <span className="eyebrow eyebrow--center eyebrow--on-dark">Before Year-End</span>
            <h2 className="display-1" style={{ marginTop: '18px' }}>Stop guessing. Get a clear answer.</h2>
            <p>One fixed fee, one engagement, a complete read on where your business stands — and a plan to act on it.</p>
            <div style={{ marginTop: '30px' }}><Link className="btn btn--primary btn--lg" to={QUALIFY} data-testid="final-cta-btn">See If You Qualify <span className="arrow">→</span></Link></div>
            <p className="finalcta__scarcity">Limited to 25 engagements this quarter · Book before September 30</p>
          </div>
        </section>
      </main>

      <BhcFooter
        engagementLinks={[
          { label: "What's included", href: '#what' },
          { label: 'How it works', href: '#how' },
          { label: 'Pricing', href: '#pricing' },
          { label: 'See if you qualify', to: QUALIFY },
          { label: 'Trust & Estate version', to: TRUST },
        ]}
      />
    </div>
  );
}
